import { useState, useEffect, useCallback } from 'react';
import signalRService from '../services/signalr';

/**
 * Custom Hook für SignalR-Verbindungen
 * @param {string} hubUrl - URL des SignalR-Hubs
 * @param {string} accessToken - Optionales Access Token für Authentifizierung
 * @returns {object} - Hook-Objekte und -Funktionen
 */
export const useSignalR = (hubUrl, accessToken = null) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);

  // Verbindung aufbauen
  const connect = useCallback(async () => {
    try {
      console.log('useSignalR: Attempting to connect with hubUrl:', hubUrl);
      console.log('useSignalR: Access token available:', !!accessToken);
      setError(null);
      await signalRService.connect(hubUrl, accessToken);
      setIsConnected(true);
      setConnectionState(signalRService.getConnectionState());
      console.log('useSignalR: Connection established successfully');
    } catch (err) {
      console.error('useSignalR: Connection failed:', err);
      setError(err.message || 'Fehler beim Verbinden mit SignalR');
      setIsConnected(false);
    }
  }, [hubUrl, accessToken]);

  // Verbindung trennen
  const disconnect = useCallback(async () => {
    try {
      await signalRService.disconnect();
      setIsConnected(false);
      setConnectionState(null);
    } catch (err) {
      setError(err.message || 'Fehler beim Trennen der SignalR-Verbindung');
    }
  }, []);

  // Message-Handler registrieren
  const onMessage = useCallback((methodName, handler) => {
    signalRService.on(methodName, handler);
  }, []);

  // Message-Handler entfernen
  const offMessage = useCallback((methodName) => {
    signalRService.off(methodName);
  }, []);

  // Nachricht senden
  const sendMessage = useCallback(async (methodName, ...args) => {
    try {
      await signalRService.send(methodName, ...args);
    } catch (err) {
      setError(err.message || 'Fehler beim Senden der Nachricht');
    }
  }, []);

  // Methode aufrufen und auf Antwort warten
  const invokeMethod = useCallback(async (methodName, ...args) => {
    try {
      return await signalRService.invoke(methodName, ...args);
    } catch (err) {
      setError(err.message || 'Fehler beim Aufrufen der Methode');
      throw err;
    }
  }, []);

  // Bei Component-Mount automatisch verbinden
  useEffect(() => {
    if (hubUrl) {
      connect();
    }

    // Cleanup bei Component-Unmount
    return () => {
      disconnect();
    };
  }, [hubUrl, connect, disconnect]);

  // Verbindungsstatus überwachen
  useEffect(() => {
    const checkConnectionState = () => {
      const state = signalRService.getConnectionState();
      setConnectionState(state);
      setIsConnected(signalRService.isConnected);
    };

    const interval = setInterval(checkConnectionState, 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    isConnected,
    connectionState,
    messages,
    error,
    connect,
    disconnect,
    onMessage,
    offMessage,
    sendMessage,
    invokeMethod,
    connectionId: signalRService.getConnectionId()
  };
};
